import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
} from '@tanstack/react-table';

import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';

import { DataTableToolbar } from './data-table-toolbar';
import { DataTablePagination } from './data-table-pagination';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import { Heading } from '@/components/ui/shared/heading';
import { Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocation } from 'react-router-dom';

export function DataTable({
  title,
  columns,
  data,
  filterableColumns = [],
  facetedFilters = [],
  onStatusFilterChange,
  loading = false,
  preselectedFutureVisits = false,
  collapsible = false,
  onRowClick,
  onPageChange,
  persistId,
}) {
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] = React.useState({});
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [sorting, setSorting] = React.useState([]);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [collapsed, setCollapsed] = React.useState(collapsible);

  const location = useLocation();
  const basePathRef = React.useRef(location.pathname.replace(/\/+$/, ''));

  // Optional override: if you pass persistId, that will scope storage instead.
  const storageScope = persistId ?? basePathRef.current;
  const storageKey = `datatable:${storageScope}:pageIndex`;

  // Clean storage if current path is outside this table’s base path
  React.useEffect(() => {
    const currentPath = location.pathname.replace(/\/+$/, '');
    const currentStorageScope = persistId ?? currentPath;
    const currentStorageKey = `datatable:${currentStorageScope}:pageIndex`;

    // Iterate over all localStorage keys
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('datatable:') && key !== currentStorageKey) {
        localStorage.removeItem(key);
      }
    });
  }, [location.pathname, persistId]);

  // Restore last page index
  const savedPageIndex = React.useMemo(() => {
    const raw = localStorage.getItem(storageKey);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [storageKey]);

  React.useEffect(() => {
    const statusFilter = columnFilters.find((f) => f.id === 'status');
    if (onStatusFilterChange) {
      onStatusFilterChange(statusFilter?.value || 'history');
    }
  }, [columnFilters, onStatusFilterChange]);

  React.useEffect(() => {
    if (preselectedFutureVisits) {
      setColumnFilters((prev) => {
        const alreadyFiltered = prev.some((f) => f.id === 'status');
        if (alreadyFiltered) return prev;
        return [...prev, { id: 'status', value: 'registrations' }];
      });
    }
  }, [preselectedFutureVisits]);

  const globalFilterFn = (row, _columnId, filterValue) => {
    const search = filterValue?.toLowerCase();
    return filterableColumns.some((key) => {
      const rawValue = row.getValue(key);
      if (!rawValue) return false;
      let formattedValue = '';
      const date = new Date(rawValue);
      const isValidDate = !isNaN(date.getTime());
      if (isValidDate && typeof rawValue !== 'string') {
        formattedValue = date.toISOString().slice(0, 10);
      } else {
        formattedValue = rawValue.toString().toLowerCase();
      }
      return formattedValue.includes(search);
    });
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 25,
        pageIndex: savedPageIndex,
      },
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: globalFilterFn,
  });

  // Persist page index
  React.useEffect(() => {
    const current = table.getState().pagination.pageIndex;
    localStorage.setItem(storageKey, String(current));
    if (onPageChange) onPageChange(current);
  }, [table.getState().pagination.pageIndex, storageKey, onPageChange]);

  // Snap to last page if data shrinks
  React.useEffect(() => {
    const pageCount = table.getPageCount();
    const current = table.getState().pagination.pageIndex;
    if (pageCount > 0 && current > pageCount - 1) {
      table.setPageIndex(pageCount - 1);
    }
  }, [data, table]);

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Heading variant="userAccountTitle">{title}</Heading>
        {collapsible && (
          <Button variant="light" onClick={() => setCollapsed((prev) => !prev)}>
            {collapsed ? 'Išskleisti' : 'Suskleisti'}
            <ChevronDown
              className={`ml-1 h-4 w-4 transition-transform ${
                collapsed ? 'rotate-[-90deg]' : ''
              }`}
            />
          </Button>
        )}
      </CardHeader>

      {!collapsed && (
        <>
          <CardContent>
            <div className="flex flex-col gap-4">
              <DataTableToolbar
                table={table}
                facetedFilters={facetedFilters}
                showSearch={true}
                searchValue={globalFilter}
                onSearchChange={setGlobalFilter}
              ></DataTableToolbar>
              <div>
                <Table>
                  <TableHeader className="bg-gray-50">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id} colSpan={header.colSpan}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-40 text-center"
                        >
                          <div className="flex items-center justify-center gap-2">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            <span>Kraunama...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : table.getRowModel().rows.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={
                            row.getIsSelected() ? 'selected' : undefined
                          }
                          onClick={() => onRowClick?.(row.original)}
                          className="hover:bg-muted cursor-pointer transition-colors"
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          Nėra rezultatų.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <DataTablePagination table={table} />
          </CardFooter>
        </>
      )}
    </Card>
  );
}
